import { getConsent, getConsentCookieDomain } from './consent';

export const FIRST_TOUCH_UTM_COOKIE_NAME = 'bookeper_first_touch_utm';
export const FIRST_TOUCH_UTM_MAX_AGE_SECONDS = 180 * 24 * 60 * 60;

export const FIRST_TOUCH_UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const;

export type FirstTouchUtmKey = (typeof FIRST_TOUCH_UTM_KEYS)[number];
export type FirstTouchUtms = Partial<Record<FirstTouchUtmKey, string>>;

interface CookieDocument {
  cookie: string;
}

interface BrowserWindow {
  location?: {
    search?: string;
    pathname?: string;
  };
}

function getBrowserDocument(): CookieDocument | undefined {
  return (globalThis as typeof globalThis & { document?: CookieDocument }).document;
}

function getBrowserWindow(): BrowserWindow | undefined {
  return (globalThis as typeof globalThis & { window?: BrowserWindow }).window;
}

function findCookieValue(name: string): string | null {
  const browserDocument = getBrowserDocument();
  if (!browserDocument) return null;

  for (const cookie of browserDocument.cookie.split(';')) {
    const trimmed = cookie.trim();
    if (!trimmed.startsWith(`${name}=`)) continue;
    return trimmed.slice(name.length + 1);
  }

  return null;
}

function isUtmKey(key: string): key is FirstTouchUtmKey {
  return (FIRST_TOUCH_UTM_KEYS as readonly string[]).includes(key);
}

export function parseUtmsFromSearch(search?: string): FirstTouchUtms {
  const source = typeof search === 'string' ? search : (getBrowserWindow()?.location?.search ?? '');
  if (!source) return {};

  const params = new URLSearchParams(source.startsWith('?') ? source : `?${source}`);
  const result: FirstTouchUtms = {};
  for (const key of FIRST_TOUCH_UTM_KEYS) {
    const value = params.get(key)?.trim();
    if (value) result[key] = value;
  }
  return result;
}

export function normalizeFirstTouchUtms(value: unknown): FirstTouchUtms {
  if (!value || typeof value !== 'object') return {};
  const result: FirstTouchUtms = {};
  for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
    if (!isUtmKey(key) || typeof raw !== 'string') continue;
    const trimmed = raw.trim();
    if (trimmed) result[key] = trimmed;
  }
  return result;
}

export function hasFirstTouchUtms(utms: FirstTouchUtms): boolean {
  return FIRST_TOUCH_UTM_KEYS.some((key) => Boolean(utms[key]));
}

export function mergeUtms(...sources: FirstTouchUtms[]): FirstTouchUtms {
  const merged: FirstTouchUtms = {};
  for (const source of sources) {
    for (const key of FIRST_TOUCH_UTM_KEYS) {
      const value = source[key];
      if (value && !merged[key]) merged[key] = value;
    }
  }
  return merged;
}

function buildFirstTouchCookie(value: FirstTouchUtms, maxAge: number): string {
  const domain = getConsentCookieDomain();
  const parts = [
    `${FIRST_TOUCH_UTM_COOKIE_NAME}=${encodeURIComponent(JSON.stringify(value))}`,
    `Max-Age=${maxAge}`,
    'Path=/',
    'SameSite=Lax',
    'Secure',
  ];
  if (domain) parts.push(`Domain=${domain}`);
  return parts.join('; ');
}

export function getFirstTouchUtms(): FirstTouchUtms {
  const raw = findCookieValue(FIRST_TOUCH_UTM_COOKIE_NAME);
  if (!raw) return {};
  try {
    return normalizeFirstTouchUtms(JSON.parse(decodeURIComponent(raw)));
  } catch {
    return {};
  }
}

let rememberedFirstTouch: FirstTouchUtms = {};

function sanitizeHandoffValue(raw: string): string | null {
  const value = raw.trim();
  // Campaign labels only: no URLs, email addresses, control characters or query fragments.
  // eslint-disable-next-line no-control-regex -- the point is to refuse control characters
  if (value.length > 200 || /[\u0000-\u001f\u007f@/?#=&]/.test(value)) return null;
  return value;
}

function firstTouchFromSignupSearch(search: string): FirstTouchUtms | null | undefined {
  const params = new URLSearchParams(search);
  const keys = [...params.keys()].filter((key) => key.startsWith('first_utm_'));
  if (keys.length === 0) return undefined;
  if (
    !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?auth\/sign-up\/?$/.test(
      getBrowserWindow()?.location?.pathname ?? ''
    ) ||
    keys.length !== FIRST_TOUCH_UTM_KEYS.length ||
    FIRST_TOUCH_UTM_KEYS.some((key) => params.getAll(`first_${key}`).length !== 1) ||
    keys.some((key) => !FIRST_TOUCH_UTM_KEYS.some((utm) => key === `first_${utm}`))
  )
    return null;
  const first: FirstTouchUtms = {};
  for (const key of FIRST_TOUCH_UTM_KEYS) {
    const value = sanitizeHandoffValue(params.get(`first_${key}`)!);
    if (value === null) return null;
    if (value) first[key] = value;
  }
  return hasFirstTouchUtms(first) ? first : null;
}

function rememberFirstTouch(utms: FirstTouchUtms): FirstTouchUtms {
  rememberedFirstTouch = mergeUtms(rememberedFirstTouch, utms);
  return rememberedFirstTouch;
}

function hasAcceptedAnalyticsConsent(): boolean {
  return getConsent()?.status === 'accepted';
}

function hasDeclinedAnalyticsConsent(): boolean {
  return getConsent()?.status === 'declined';
}

function writeFirstTouchCookie(value: FirstTouchUtms, maxAge: number): void {
  const browserDocument = getBrowserDocument();
  if (!browserDocument) return;
  browserDocument.cookie = buildFirstTouchCookie(value, maxAge);
}

/** Drop the first-touch cookie and session memory (decline / withdraw). */
export function clearFirstTouchUtms(): void {
  rememberedFirstTouch = {};
  writeFirstTouchCookie({}, 0);
}

function persistRememberedFirstTouch(): FirstTouchUtms {
  const existing = getFirstTouchUtms();
  if (hasFirstTouchUtms(existing)) {
    rememberFirstTouch(existing);
    return existing;
  }
  if (!hasFirstTouchUtms(rememberedFirstTouch)) return {};
  writeFirstTouchCookie(rememberedFirstTouch, FIRST_TOUCH_UTM_MAX_AGE_SECONDS);
  return rememberedFirstTouch;
}

/** Write the in-memory first touch only after an explicit accept. */
export function persistFirstTouchUtmsAfterAccept(search?: string): FirstTouchUtms {
  // Accept may be the first reader on a fresh signup document. Keep the same
  // envelope validation as pending capture rather than remembering last touch.
  return captureFirstTouchUtms(search);
}

/**
 * Remember first-touch UTMs for this session. The `.bookeper.pl` cookie is
 * analytics storage — persist only after accept; expire leftovers otherwise.
 * A persisted decline must not keep session memory: the consent restorer
 * skips revoke when the SDK is already DENIED, so this path is the clear.
 */
export function captureFirstTouchUtms(search?: string): FirstTouchUtms {
  if (hasDeclinedAnalyticsConsent()) {
    clearFirstTouchUtms();
    return {};
  }

  const currentSearch = search ?? getBrowserWindow()?.location?.search ?? '';
  const handedOff = firstTouchFromSignupSearch(currentSearch);
  if (handedOff !== null) rememberFirstTouch(handedOff ?? parseUtmsFromSearch(currentSearch));

  if (hasAcceptedAnalyticsConsent()) {
    return persistRememberedFirstTouch();
  }

  if (hasFirstTouchUtms(getFirstTouchUtms())) {
    writeFirstTouchCookie({}, 0);
  }
  return rememberedFirstTouch;
}

/**
 * Attribution for signup links and events: current-URL UTMs win, then the
 * first-touch cookie fills any missing keys so SPA navigations do not drop
 * the original campaign. Declined visitors get no attribution at all.
 *
 * Do not write this into user_metadata / `$initial_utm_*`. Those must come
 * from {@link getFirstTouchAttribution} so a later campaign URL cannot race
 * the cookie first-touch through `$set_once`.
 */
export function getAttributionUtms(search?: string): FirstTouchUtms {
  if (hasDeclinedAnalyticsConsent()) {
    clearFirstTouchUtms();
    return {};
  }
  const current = parseUtmsFromSearch(search);
  const firstTouch = captureFirstTouchUtms(search);
  return mergeUtms(current, firstTouch);
}

/**
 * First-wins campaign for person `$initial_utm_*` and auth user_metadata.
 * Current-URL keys never override a remembered or cookied first touch.
 */
export function getFirstTouchAttribution(search?: string): FirstTouchUtms {
  if (hasDeclinedAnalyticsConsent()) {
    clearFirstTouchUtms();
    return {};
  }
  return captureFirstTouchUtms(search);
}

export function appendUtmsToUrl(basePath: string, utms: FirstTouchUtms): string {
  const baseUrl = new URL(basePath, 'https://bookeper.local');
  const nextParams = new URLSearchParams(baseUrl.search);

  for (const key of FIRST_TOUCH_UTM_KEYS) {
    if (nextParams.has(key)) continue;
    const value = utms[key];
    if (value) nextParams.set(key, value);
  }

  const query = nextParams.toString();
  const nextPath = `${baseUrl.pathname}${query ? `?${query}` : ''}${baseUrl.hash}`;
  if (/^https?:\/\//.test(basePath)) {
    return `${baseUrl.origin}${nextPath}`;
  }
  if (basePath.startsWith('//')) return `//${baseUrl.host}${nextPath}`;
  return nextPath;
}

/** A complete sanitized envelope carries first touch only to our signup surface. */
export function appendSignupAttributionToUrl(
  basePath: string,
  current: FirstTouchUtms,
  first: FirstTouchUtms
): string {
  const withCurrent = appendUtmsToUrl(basePath, current);
  const url = new URL(withCurrent, 'https://app.bookeper.pl');
  const internal = basePath.startsWith('/') && !basePath.startsWith('//');
  const trusted =
    (internal || url.origin === 'https://app.bookeper.pl') &&
    /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?auth\/sign-up\/?$/.test(url.pathname);
  if (
    !trusted ||
    !hasFirstTouchUtms(first) ||
    FIRST_TOUCH_UTM_KEYS.every((key) => first[key] === current[key])
  )
    return withCurrent;
  const values = FIRST_TOUCH_UTM_KEYS.map((key) => sanitizeHandoffValue(first[key] ?? ''));
  if (values.some((value) => value === null)) return withCurrent;
  for (const [index, key] of FIRST_TOUCH_UTM_KEYS.entries())
    url.searchParams.set(`first_${key}`, values[index]!);
  const next = `${url.pathname}${url.search}${url.hash}`;
  return internal ? next : `${url.origin}${next}`;
}

export function personSetOnceFromUtms(utms: FirstTouchUtms): Record<string, string> {
  const setOnce: Record<string, string> = {};
  if (utms.utm_source) setOnce.$initial_utm_source = utms.utm_source;
  if (utms.utm_medium) setOnce.$initial_utm_medium = utms.utm_medium;
  if (utms.utm_campaign) setOnce.$initial_utm_campaign = utms.utm_campaign;
  if (utms.utm_content) setOnce.$initial_utm_content = utms.utm_content;
  if (utms.utm_term) setOnce.$initial_utm_term = utms.utm_term;
  return setOnce;
}

/**
 * Referrer host hand-off (BOO-5163). The www landing passes the visitor's EXTERNAL referrer host to
 * the app in this param, because the app's own `document.referrer` is www by then and the original
 * source (Google, a partner, an ad network) would be lost. Host only, never a URL.
 */
export const REFERRER_HOST_PARAM = 'ref_host';

// Bare lowercase hostname, at least one dot: no scheme, path, port, userinfo or query, so nothing
// but a domain name can ride into analytics or into a link.
const REFERRER_HOST = /^(?=.{4,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,24}$/;
const OWN_DOMAIN = /(?:^|\.)bookeper\.pl$/;

/** A referrer host worth reporting: well-formed, and not one of our own surfaces. */
export function cleanReferrerHost(raw: unknown): string | undefined {
  if (typeof raw !== 'string') return undefined;
  const host = raw.trim().toLowerCase();
  return REFERRER_HOST.test(host) && !OWN_DOMAIN.test(host) ? host : undefined;
}

/** Host of a `document.referrer` string, or undefined for empty, own-domain or malformed ones. */
export function externalReferrerHost(referrer: string | null | undefined): string | undefined {
  if (!referrer) return undefined;
  try {
    return cleanReferrerHost(new URL(referrer).hostname);
  } catch {
    return undefined;
  }
}
